import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, KeyRound } from 'lucide-react';
import { useAuth } from '../stores/auth';
import { tokenStore } from '../lib/api';
import { AuthShell, AuthSubmit, AuthNote } from '../components/AuthShell';
import { markIntroPending, releaseBootLoader } from '../lib/appReady';
import { Field, Input } from '../components/site/Form';
import { INTERIORS } from '../lib/site';
import { inviteApi } from '../lib/resident';
import type { AuthUser } from '../types/api';
import { PhoneInput } from '../components/ui/PhoneInput';

/**
 * Resident sign-up with the invite code from the welcome letter (prototype §26a). Public. The code says which
 * home it is for; the resident sets a password (and, if nobody is on the tenancy yet, their name and email) and
 * is signed straight in to their portal.
 */
export default function SignupPage() {
  const { code: rawCode } = useParams<{ code?: string }>();
  const code = (rawCode ?? '').trim().toUpperCase();
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', password2: '', terms: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { releaseBootLoader(true); }, []);

  const invite = useQuery({ queryKey: ['invite', code], queryFn: () => inviteApi.validate(code), enabled: !!code, retry: false });

  if (user && !busy) return <Navigate to="/app" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (invite.data?.needsDetails && !form.firstName.trim()) return setError('Put in your name.');
    if (invite.data?.needsDetails && !form.email.trim()) return setError('Put in your email address.');
    if (form.password.length < 8) return setError('Use at least eight characters for your password.');
    if (form.password !== form.password2) return setError('The two passwords do not match.');
    if (!form.terms) return setError('Please accept the terms of use to create your account.');
    setBusy(true);
    try {
      const s = await inviteApi.accept(code, {
        password: form.password, phone: form.phone.trim() || null, acceptTerms: true,
        ...(invite.data?.needsDetails ? { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() } : {}),
      });
      tokenStore.set({ accessToken: s.accessToken, refreshToken: s.refreshToken });
      useAuth.setState({ user: s.user as AuthUser, loading: false, error: null });
      markIntroPending();
      navigate('/app/my-home', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — try again.');
      setBusy(false);
    }
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: k === 'terms' ? e.target.checked : e.target.value });

  return (
    <AuthShell image={INTERIORS.harlow} title={<>Resident <em>sign-up</em></>} subtitle={!code ? 'Enter the invite code from your welcome email.' : undefined}
      below={<p>Already registered? <Link to="/login" className="copy-link font-medium">Sign in</Link></p>}>
      {!code && (
        <form onSubmit={(e) => { e.preventDefault(); if (typed.trim()) navigate(`/signup/${typed.trim().toUpperCase().replace(/[\s-]+/g, '')}`); }} className="space-y-5">
          <Field label="Invite code">
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="K7M2QXAB" autoCapitalize="characters" autoComplete="off" className="font-mono uppercase tracking-[0.18em]" />
          </Field>
          <div className="pt-1"><AuthSubmit disabled={!typed.trim()}>Continue</AuthSubmit></div>
        </form>
      )}

      {code && invite.isLoading && <p className="text-[14px] text-ink-400">Checking your code…</p>}

      {code && invite.isError && (
        <div className="space-y-4">
          <AuthNote>{(invite.error as Error).message}</AuthNote>
          <p className="text-[14px] leading-relaxed text-ink-500">Code <span className="font-mono font-semibold tracking-wider text-ink-800">{code}</span>. Check it against your welcome email, or <Link to="/signup" className="copy-link font-medium">type it again</Link>.</p>
        </div>
      )}

      {code && invite.data && (
        <>
          <div className="flex items-start gap-4 rounded-[12px] border border-bronze-200/70 bg-bronze-50 p-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-bronze-600"><Home className="h-4 w-4" /></div>
            <div className="min-w-0 text-[14px] leading-relaxed text-ink-700">
              <div className="font-display text-[1.35rem] font-medium leading-tight text-ink-900">{invite.data.firstName ? `Hello ${invite.data.firstName}` : 'Hello'}</div>
              <div className="mt-0.5">Flat <span className="font-medium">{invite.data.unit}</span> · {invite.data.scheme}</div>
              <div className="mt-1 flex items-center gap-1.5 font-mono text-[12px] tracking-wider text-ink-500"><KeyRound className="h-3.5 w-3.5" /> {code}</div>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" autoComplete="on">
            {invite.data.needsDetails && (
              <>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="First name"><Input value={form.firstName} onChange={set('firstName')} autoComplete="given-name" /></Field>
                  <Field label="Last name"><Input value={form.lastName} onChange={set('lastName')} autoComplete="family-name" /></Field>
                </div>
                <Field label="Email address"><Input type="email" value={form.email} onChange={set('email')} autoComplete="email" /></Field>
              </>
            )}
            <Field label="Choose a password"><Input type="password" value={form.password} onChange={set('password')} autoComplete="new-password" placeholder="At least 8 characters" /></Field>
            <Field label="Repeat it"><Input type="password" value={form.password2} onChange={set('password2')} autoComplete="new-password" /></Field>
            <Field label="Mobile number (optional)"><PhoneInput kit="site" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} /></Field>
            <label className="site-check">
              <input type="checkbox" checked={form.terms} onChange={set('terms')} />
              <span className="text-[14px] text-ink-600">I agree to the resident portal terms of use and the Lotus Living privacy notice. <span className="text-ink-400">(Wording to follow — REQUIRES BUSINESS DECISION.)</span></span>
            </label>
            {error && <AuthNote>{error}</AuthNote>}
            <div className="pt-1"><AuthSubmit disabled={busy}>{busy ? 'Creating your account…' : 'Create my account'}</AuthSubmit></div>
          </form>
        </>
      )}
    </AuthShell>
  );
}
